import React, { FormEvent, useState } from 'react';
import { config } from '../../config';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

export const EmailRegistration = () => {
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    if (formData.get('_gotcha')) {
      return;
    }

    try {
      setSubmitState('submitting');
      setErrorMessage('');

      const response = await fetch(config.email_registration_endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Unable to register your email right now. Please try again later.');
      }

      setSubmitState('success');
      form.reset();
    } catch (error) {
      setSubmitState('error');
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <section id="register" className="section-padding bg-white">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto rounded-[3rem] bg-secondary text-white p-10 sm:p-16 border border-white/10 shadow-2xl">
          <p className="text-primary font-bold tracking-[0.3em] uppercase text-xs mb-4">Stay Updated</p>
          <h2 className="text-3xl sm:text-5xl font-bold mb-4">Email Registration</h2>
          <p className="text-white/70 mb-10">
            Register your email to receive church updates, event reminders, and important announcements.
          </p>

          {submitState === 'success' ? (
            <div className="rounded-2xl bg-green-500/10 border border-green-300/30 p-6">
              <p className="font-semibold text-green-100">Thank you. Your email has been registered successfully.</p>
              <button
                type="button"
                onClick={() => setSubmitState('idle')}
                className="mt-4 text-primary font-semibold hover:underline"
              >
                Register another email
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" />
              <input type="hidden" name="_subject" value="New Email Registration" />
              <input type="hidden" name="source" value="Mobile App Registration" />

              <div className="grid sm:grid-cols-[1fr_auto] gap-4">
                <div>
                  <label htmlFor="register-email" className="block text-xs font-bold uppercase tracking-widest text-white/60 mb-2">
                    Email Address
                  </label>
                  <input
                    id="register-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full px-5 py-4 rounded-2xl bg-white text-secondary border border-secondary/10 focus:border-primary outline-none"
                  />
                </div>
                <button type="submit" disabled={submitState === 'submitting'} className="btn-primary self-end disabled:opacity-70">
                  {submitState === 'submitting' ? 'Registering...' : 'Register'}
                </button>
              </div>

              {submitState === 'error' && (
                <p role="alert" className="text-sm text-red-200">{errorMessage}</p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
